import type { components } from '@tms/api-client';

import { setSession } from '../auth/sessionStore';
import { api } from './client';
import { readFailure } from './problem';
import type { ApiFailure } from './problem';
import { allowRefreshAgain } from './refresh';

/**
 * The identity endpoints, one function each (`FEAT-001`, `FEAT-003`, `FEAT-005`).
 *
 * Every function answers the same shape — a result that is either `ok` with the body or not ok with an
 * `ApiFailure` — so a screen never sees a thrown exception for an expected outcome. A wrong password is
 * not exceptional; it is one of the outcomes.
 */
export type ApiResult<T> = { readonly ok: true; readonly value: T } | { readonly ok: false; readonly failure: ApiFailure };

export interface RegisterInput {
  readonly displayName: string;
  readonly email: string;
  readonly password: string;
  readonly acceptedTerms: boolean;
}

/** `FEAT-001 §5`. The response is identical whether or not the address was already held. */
export async function register(
  input: RegisterInput,
): Promise<ApiResult<{ email: string; codeExpiresInMinutes: number }>> {
  return call(async () => {
    const { data, error, response } = await api.POST('/v1/auth/register', { body: input });

    if (data) {
      return {
        ok: true,
        value: { email: data.email ?? input.email, codeExpiresInMinutes: data.codeExpiresInMinutes ?? 10 },
      };
    }

    return { ok: false, failure: readFailure(error, response.status) };
  });
}

/** `FEAT-001 §5.1`. Every way of being wrong is one `422` with one message (`FEAT-001 §3.13`). */
export async function verifyEmail(input: { email: string; code: string }): Promise<ApiResult<{ email: string }>> {
  return call(async () => {
    const { data, error, response } = await api.POST('/v1/auth/verify-email', { body: input });

    if (data) {
      return { ok: true, value: { email: data.email ?? input.email } };
    }

    return { ok: false, failure: readFailure(error, response.status) };
  });
}

/**
 * `FEAT-001 §5.2`. Issues a fresh code and consumes every outstanding one, and answers the same whether or
 * not the address is held.
 */
export async function resendCode(
  input: { email: string },
): Promise<ApiResult<{ email: string; codeExpiresInMinutes: number }>> {
  return call(async () => {
    const { data, error, response } = await api.POST('/v1/auth/resend-code', { body: input });

    if (data) {
      return {
        ok: true,
        value: { email: data.email ?? input.email, codeExpiresInMinutes: data.codeExpiresInMinutes ?? 10 },
      };
    }

    return { ok: false, failure: readFailure(error, response.status) };
  });
}

export interface Session {
  readonly accessToken: string;
  readonly expiresInSeconds: number;
  /** `FEAT-003 §3.10`. `ONB-04` reads this and sends an unverified account to `ONB-03`. */
  readonly emailVerified: boolean;
}

/**
 * `FEAT-003 §5`. Four ways to fail and one `401` for all of them (`FEAT-003 §3.9`).
 *
 * The refresh token is not in the body. It arrives as an `HttpOnly; Secure; SameSite=Strict` cookie that
 * no script on the page can read, which is the whole point of it being opaque.
 *
 * **Recording the session is this function's job, not the screen's.** A screen that had to remember to do
 * it could forget, and the failure would be a person signed in with an app that does not think so. It
 * also lifts the latch on refresh: a new sign-in sets a new cookie, and the last refusal says nothing
 * about it.
 */
export async function signIn(input: { email: string; password: string }): Promise<ApiResult<Session>> {
  return call(async () => {
    const { data, error, response } = await api.POST('/v1/auth/token', { body: input });

    if (data?.accessToken) {
      allowRefreshAgain();
      setSession({ accessToken: data.accessToken, emailVerified: data.emailVerified ?? false });

      return {
        ok: true,
        value: {
          accessToken: data.accessToken,
          expiresInSeconds: data.expiresInSeconds ?? 0,
          emailVerified: data.emailVerified ?? false,
        },
      };
    }

    return { ok: false, failure: readFailure(error, response.status) };
  });
}

/** `FEAT-005 §5.1`. Answers identically whether or not the address is held. */
export async function forgotPassword(
  input: { email: string },
): Promise<ApiResult<{ email: string; codeExpiresInMinutes: number }>> {
  return call(async () => {
    const { data, error, response } = await api.POST('/v1/auth/password/forgot', { body: input });

    if (data) {
      return {
        ok: true,
        value: { email: data.email ?? input.email, codeExpiresInMinutes: data.codeExpiresInMinutes ?? 15 },
      };
    }

    return { ok: false, failure: readFailure(error, response.status) };
  });
}

/**
 * `FEAT-005 §5.2`. Answers `204` and no tokens: a reset is not a sign-in, and going back to `ONB-04` is
 * the step that proves the new password can be used (`FEAT-005 §3.1`).
 */
export async function resetPassword(
  input: { email: string; code: string; newPassword: string },
): Promise<ApiResult<null>> {
  return call(async () => {
    const { error, response } = await api.POST('/v1/auth/password/reset', { body: input });

    if (response.ok) {
      return { ok: true, value: null };
    }

    return { ok: false, failure: readFailure(error, response.status) };
  });
}

/** The account, as `GET /me` and `PATCH /me/preferences` both answer it (`FEAT-008 §5.1`). */
export type Account = components['schemas']['MeResponse'];

/** Where a ledger lives (`ONB-06`). The names, not the numbers — `STD-003 §1`. */
export type StorageMode = components['schemas']['StorageMode'];

/**
 * What `ONB-05` sends. Every field optional: absent means unchanged (`FEAT-008 §3.1`).
 *
 * **Derived from the generated contract rather than written out beside it**, so a field the API drops
 * becomes a compile error here instead of a request that is accepted and ignored. That is not
 * hypothetical: `storageMode` used to be listed here by hand, `FEAT-008 §11` moved it to
 * `PATCH /me/storage-mode`, and because the body below is cast on its way out nothing objected — `ONB-06`
 * went on sending it, the API answered `200`, and somebody who chose *Local only* was left on Hybrid with
 * the server copy they had just asked not to have. `ONB-06` calls {@link changeStorageMode} now, and this
 * type would no longer let it do anything else.
 *
 * The generated command types every field as nullable rather than optional, which is the same thing over
 * the wire and a different thing in TypeScript; this is that shape restated as "absent means unchanged".
 */
export type PreferencesInput = {
  readonly [K in keyof components['schemas']['UpdatePreferencesCommand']]?: NonNullable<
    components['schemas']['UpdatePreferencesCommand'][K]
  >;
};

/** `FEAT-008 §5.1`. */
export async function getMe(): Promise<ApiResult<Account>> {
  return call(async () => {
    const { data, error, response } = await api.GET('/v1/me');

    return data ? { ok: true, value: data } : { ok: false, failure: readFailure(error, response.status) };
  });
}

/**
 * `FEAT-008 §5.2`. Answers with the whole account, which is what lets `ONB-06` hand `ONB-07` its trial
 * date without a second request.
 */
export async function updatePreferences(input: PreferencesInput): Promise<ApiResult<Account>> {
  return call(async () => {
    const { data, error, response } = await api.PATCH('/v1/me/preferences', {
      // Cast because the generated command wants every key present-and-nullable and this sends only what
      // the screen set, which is what makes "absent means unchanged" true. `PreferencesInput` is derived
      // from that same command, so the cast widens optionality and cannot smuggle in a field the API does
      // not have.
      body: input as never,
    });

    return data ? { ok: true, value: data } : { ok: false, failure: readFailure(error, response.status) };
  });
}

/** What `PATCH /me/storage-mode` answers — the mode, and where the server copy stands (`FEAT-013 §5.1`). */
export type StorageModeState = components['schemas']['StorageModeResponse'];

/**
 * `FEAT-013 §5.1` — `ONB-06` and `SET-05`.
 *
 * Its own route rather than a field on `PATCH /me/preferences`, and the split is the point rather than
 * tidiness: choosing Local schedules the deletion of the server copy, and this is the only endpoint that
 * can (`FEAT-008 §11`).
 *
 * It answers `200` and deletes nothing. What it writes is an intent, carried out by a job inside 24 hours —
 * so `serverCopyPurgedAtUtc` is null on the way back and that is correct, not a failure.
 */
export async function changeStorageMode(mode: StorageMode): Promise<ApiResult<StorageModeState>> {
  return call(async () => {
    const { data, error, response } = await api.PATCH('/v1/me/storage-mode', {
      body: { storageMode: mode } as never,
    });

    return data ? { ok: true, value: data } : { ok: false, failure: readFailure(error, response.status) };
  });
}

/**
 * Runs a call and turns a *transport* failure into a result rather than a thrown error.
 *
 * A dropped connection is not exceptional in a phone-first app — it is Tuesday on a train. Every screen
 * has to render it, so it arrives the same way every other outcome does. `readFailure` gives it the
 * `unknown` kind, and the copy for that says what to do next.
 */
async function call<T>(run: () => Promise<ApiResult<T>>): Promise<ApiResult<T>> {
  try {
    return await run();
  } catch {
    return { ok: false, failure: readFailure(undefined) };
  }
}
